import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const MONEY_FORMATTER = new Intl.NumberFormat('es-CL', {
  currency: 'CLP',
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
  style: 'currency',
});

/** Builds a native Shopify bundle from one Firenze preset and its selected colours. */
class AteleaBundleBuilder extends HTMLElement {
  /** @type {BuilderData} */
  data = { products: [], presets: [], parents: [], pairDiscount: 0.1, completeDiscount: 0.15 };

  /** @type {{selectedKeys: string[], colors: Record<string, string>}} */
  state = { selectedKeys: [], colors: {} };

  /** @type {{openStep: number}} */
  flow = { openStep: 1 };

  /** @type {boolean} */
  isAdding = false;

  /** @type {number} */
  previewRequestId = 0;

  /** @type {number} */
  previewDelayTimer = 0;

  /** @type {number} */
  previewFinishTimer = 0;

  /** @type {AbortController | null} */
  previewImageAbortController = null;

  /** @type {Set<string>} */
  wishlistKeys = new Set();

  /** @type {MediaQueryList | null} */
  flowMediaQuery = null;

  connectedCallback() {
    const dataElement = this.querySelector('[data-atelea-bundle-data]');
    if (!dataElement) return;

    this.flowMediaQuery = window.matchMedia('(max-width: 749px)');
    this.flowMediaQuery.addEventListener('change', this.handleFlowBreakpoint);
    const isPrelaunch = this.dataset.ateleaPrelaunchMode === 'true';
    if (isPrelaunch) {
      window.addEventListener('atelea:wishlist:changed', this.handleWishlistChanged);
    }

    try {
      this.data = JSON.parse(dataElement.textContent || '{}');
    } catch (error) {
      console.error('[bundle-builder] Invalid section data:', error);
      this.setStatus(this.dataset.loadError || '', 'error');
      return;
    }

    this.buildColorOptions();
    this.bindEvents();
    this.renderPresetPrices();
    this.restorePresetFromUrl();
    if (!this.state.selectedKeys.length) this.restoreDefaultPreset();
    this.render();
    if (isPrelaunch) window.dispatchEvent(new CustomEvent('atelea:wishlist:request-state'));
  }

  disconnectedCallback() {
    this.previewRequestId += 1;
    this.clearPreviewTimers();
    this.cancelPreviewImageWait();
    const media = this.querySelector('[data-preview-media]');
    if (media instanceof HTMLElement) {
      const activeImage = media.querySelector('img[data-preview-active]');
      const pendingImage = media.querySelector('img[data-preview-target]');
      if (media.dataset.previewing === 'true' && pendingImage) {
        activeImage?.remove();
        pendingImage.classList.remove('atelea-bundle-builder__preview-image--target');
        pendingImage.classList.remove('atelea-bundle-builder__preview-image--previous');
        pendingImage.removeAttribute('data-preview-target');
        pendingImage.removeAttribute('aria-hidden');
        pendingImage.dataset.previewActive = '';
      } else {
        pendingImage?.remove();
        activeImage?.classList.remove('atelea-bundle-builder__preview-image--previous');
      }
      media.removeAttribute('data-previewing');
      media.removeAttribute('data-delayed');
      media.removeAttribute('data-state');
      media.removeAttribute('aria-busy');
    }
    this.flowMediaQuery?.removeEventListener('change', this.handleFlowBreakpoint);
    if (this.dataset.ateleaPrelaunchMode === 'true') {
      window.removeEventListener('atelea:wishlist:changed', this.handleWishlistChanged);
    }
    this.flowMediaQuery = null;
  }

  handleFlowBreakpoint = () => {
    this.render();
  };

  /** @param {CustomEvent<WishlistChangedDetail>} event */
  handleWishlistChanged = (event) => {
    const items = event.detail?.items || [];
    this.wishlistKeys = new Set(
      items
        .filter((item) => item && item.productHandle)
        .map((item) => `${item.productHandle}:${item.variantId || 'default'}`)
    );
    this.renderAddButtons(Boolean(this.getSelectionMatch()?.valid));
  };

  bindEvents() {
    this.addEventListener('click', this.handleClick);
  }

  /** @param {MouseEvent} event */
  handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const stepToggle = target?.closest('[data-builder-step-toggle]');
    const continueControl = target?.closest('[data-builder-continue]');
    const preset = target?.closest('[data-preset-id]');
    const color = target?.closest('[data-color-option]');
    const bulkColor = target?.closest('[data-bulk-color]');
    const addButton = target?.closest('[data-add-bundle]');

    if (stepToggle) {
      this.toggleStep(Number(stepToggle.getAttribute('data-builder-step-toggle')));
      return;
    }

    if (continueControl) {
      this.confirmStep(Number(continueControl.getAttribute('data-builder-continue')));
      return;
    }

    if (preset) {
      this.selectPreset(preset.dataset.presetId || '');
      return;
    }

    if (color) {
      this.selectColor(color.dataset.productKey || '', color.dataset.color || '');
      return;
    }

    if (bulkColor) {
      this.applyColorToPack(bulkColor.dataset.bulkColor || '');
      return;
    }

    if (addButton) {
      if (this.dataset.ateleaPrelaunchMode === 'true') {
        this.toggleBundleWishlist();
      } else {
        this.addBundleToCart();
      }
    }
  };

  buildColorOptions() {
    const colors = this.getColors();

    this.querySelectorAll('[data-color-options]').forEach((container) => {
      const productKey = container.getAttribute('data-color-options') || '';
      container.replaceChildren(...this.createColorButtons(productKey, colors));
    });

    const bulkContainer = this.querySelector('[data-bulk-color-options]');
    if (bulkContainer) {
      bulkContainer.replaceChildren(...colors.map((color) => this.createBulkColorButton(color)));
    }
  }

  /**
   * @param {string} productKey
   * @param {ColorOption[]} colors
   * @returns {HTMLButtonElement[]}
   */
  createColorButtons(productKey, colors) {
    const product = this.getProduct(productKey);
    if (!product) return [];

    return colors
      .filter((color) => product.variants.some((variant) => variant.color === color.name))
      .map((color) => {
        const button = document.createElement('button');
        button.className = 'atelea-bundle-builder__color-option';
        button.type = 'button';
        button.dataset.colorOption = '';
        button.dataset.productKey = productKey;
        button.dataset.color = color.name;
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute('aria-label', color.name);

        const swatch = document.createElement('span');
        swatch.className = 'atelea-bundle-builder__swatch';
        swatch.dataset.colorSlug = color.slug;
        swatch.setAttribute('aria-hidden', 'true');

        const label = document.createElement('span');
        label.className = 'visually-hidden';
        label.textContent = color.name;

        button.append(swatch, label);
        return button;
      });
  }

  /** @param {ColorOption} color */
  createBulkColorButton(color) {
    const button = document.createElement('button');
    button.className = 'atelea-bundle-builder__bulk-option';
    button.type = 'button';
    button.dataset.bulkColor = color.name;
    button.setAttribute(
      'aria-label',
      this.data.messages.applyColor.replace('{{ color }}', color.name)
    );

    const swatch = document.createElement('span');
    swatch.className = 'atelea-bundle-builder__swatch';
    swatch.dataset.colorSlug = color.slug;
    swatch.setAttribute('aria-hidden', 'true');

    const label = document.createElement('span');
    label.textContent = color.name;

    button.append(swatch, label);
    return button;
  }

  renderPresetPrices() {
    this.querySelectorAll('[data-preset-id]').forEach((button) => {
      const preset = this.data.presets.find((item) => item.id === button.dataset.presetId);
      const parent = preset ? this.getParent(preset.parentHandle) : null;
      const originalPrice = parent?.variants[0]?.price;
      const discount = preset?.components.filter(Boolean).length === 3
        ? this.data.completeDiscount
        : this.data.pairDiscount;
      const pricing = originalPrice == null
        ? null
        : this.getDiscountedPrices(originalPrice, discount);
      const priceElement = button.querySelector('[data-preset-price]');
      const originalElement = button.querySelector('[data-preset-original]');
      const savingElement = button.querySelector('[data-preset-saving]');

      if (priceElement && pricing) {
        priceElement.textContent = this.formatMoney(pricing.final * 100);
      }
      if (originalElement && pricing) {
        originalElement.textContent = this.formatMoney(pricing.regular * 100);
      }
      if (savingElement && pricing) {
        savingElement.textContent = this.data.messages.savings.replace(
          '{{ amount }}',
          this.formatMoney(pricing.saved * 100)
        );
      }
    });
  }

  restorePresetFromUrl() {
    const requestedPreset = new URLSearchParams(window.location.search).get('pack');
    if (!requestedPreset) return;

    const presetId = this.slugify(requestedPreset);
    const preset = this.data.presets.find((item) => item.id === presetId);
    if (preset) this.setSelectionFromPreset(preset);
  }

  restoreDefaultPreset() {
    const preset = this.data.presets.find((item) => item.recommended)
      || this.data.presets[0];
    if (preset) this.setSelectionFromPreset(preset);
  }

  /** @param {string} presetId */
  selectPreset(presetId) {
    const preset = this.data.presets.find((item) => item.id === presetId);
    if (!preset) return;

    if (this.findPresetForSelection()?.id === preset.id) return;

    this.setSelectionFromPreset(preset);
    this.flow = { openStep: 1 };
    this.clearStatus();
    this.render();
  }

  /** @param {Preset} preset */
  setSelectionFromPreset(preset) {
    const selectedKeys = this.getPresetProductKeys(preset);
    /** @type {Record<string, string>} */
    const colors = {};

    selectedKeys.forEach((key) => {
      const product = this.getProduct(key);
      const variant = product?.variants.find((item) => item.available !== false)
        || product?.variants[0];
      if (variant?.color) colors[key] = variant.color;
    });

    this.state.selectedKeys = [...new Set(selectedKeys)];
    this.state.colors = colors;
  }

  /** @param {string} productKey @param {string} color */
  selectColor(productKey, color) {
    if (!this.state.selectedKeys.includes(productKey)) return;
    const product = this.getProduct(productKey);
    const variant = product?.variants.find((item) => item.color === color);
    if (!variant || variant.available === false) return;

    this.state.colors[productKey] = color;
    this.flow.openStep = 2;
    this.clearStatus();
    this.render();
  }

  /** @param {string} color */
  applyColorToPack(color) {
    this.state.selectedKeys.forEach((key) => {
      const product = this.getProduct(key);
      if (product?.variants.some((variant) => (
        variant.color === color && variant.available !== false
      ))) {
        this.state.colors[key] = color;
      }
    });
    this.flow.openStep = 2;
    this.clearStatus();
    this.render();
  }

  /** @param {number} step */
  confirmStep(step) {
    const match = this.getSelectionMatch();

    if (step === 1) {
      if (!this.findPresetForSelection()) return;
      this.flow.openStep = 2;
      this.render();
      this.focusStep(2);
      return;
    }

    if (step === 2) {
      if (!match?.valid) {
        this.render();
        return;
      }
      this.flow.openStep = 3;
      this.render();
      this.focusStep(3);
    }
  }

  /** @param {number} step */
  toggleStep(step) {
    if (!this.isMobileViewport() || ![1, 2, 3].includes(step)) return;
    if (step === 2 && !this.findPresetForSelection()) return;
    if (step === 3 && !this.getSelectionMatch()?.valid) return;

    this.flow.openStep = this.flow.openStep === step ? 0 : step;
    this.render();
  }

  /** @param {number} step */
  focusStep(step) {
    if (!this.isMobileViewport()) return;

    window.requestAnimationFrame(() => {
      const section = this.querySelector(`[data-builder-step="${step}"]`);
      const toggle = section?.querySelector('[data-builder-step-toggle]');
      if (!(section instanceof HTMLElement) || !(toggle instanceof HTMLButtonElement)) return;

      section.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
        block: 'start',
      });
      toggle.focus({ preventScroll: true });
    });
  }

  render() {
    const match = this.getSelectionMatch();
    const hasSelection = this.state.selectedKeys.length >= 2;

    this.classList.toggle('has-selection', hasSelection);
    this.renderPresets();
    this.renderColors();
    this.renderPreview();
    this.renderSummary(match);
    this.renderFlow(match);
    this.renderAddButtons(Boolean(match?.valid));
  }

  renderPresets() {
    const selectedPresetId = this.findPresetForSelection()?.id;
    this.querySelectorAll('[data-preset-id]').forEach((button) => {
      const isSelected = button.dataset.presetId === selectedPresetId;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
    });
  }

  renderColors() {
    const hasSelection = this.state.selectedKeys.length >= 2;
    this.querySelector('[data-color-empty]')?.toggleAttribute('hidden', hasSelection);
    this.querySelector('[data-color-picker]')?.toggleAttribute('hidden', !hasSelection);

    this.querySelectorAll('[data-color-row]').forEach((row) => {
      const productKey = row.getAttribute('data-color-row') || '';
      const isSelected = this.state.selectedKeys.includes(productKey);
      row.toggleAttribute('hidden', !isSelected);
      if (!isSelected) return;

      const product = this.getProduct(productKey);
      const color = this.state.colors[productKey];
      const variant = product?.variants.find((item) => item.color === color);
      const displayPrice = variant?.price || product?.variants[0]?.price;
      const current = row.querySelector('[data-color-current]');
      const price = row.querySelector('[data-color-price]');
      const icon = row.querySelector('[data-color-icon]');

      if (current) {
        current.textContent = color || this.data.messages.chooseColor;
      }
      if (price) {
        price.textContent = color && displayPrice != null
          ? this.formatMoney(displayPrice)
          : '—';
      }
      if (icon instanceof HTMLElement) {
        icon.dataset.colorSlug = color ? this.slugify(color) : '';
        const imageUrl = variant?.image || '';
        let image = icon.querySelector('.atelea-bundle-builder__color-image');

        if (imageUrl && !(image instanceof HTMLImageElement)) {
          icon.replaceChildren();
          image = document.createElement('img');
          image.className = 'atelea-bundle-builder__color-image';
          image.loading = 'lazy';
          icon.append(image);
        }

        if (imageUrl && image instanceof HTMLImageElement) {
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src = imageUrl;
          image.alt = [product?.title, color].filter(Boolean).join(' · ');
        }
      }
    });

    this.querySelectorAll('[data-color-option]').forEach((button) => {
      const productKey = button.dataset.productKey || '';
      const color = button.dataset.color || '';
      const product = this.getProduct(productKey);
      const variant = product?.variants.find((item) => item.color === color);
      const selected = this.state.colors[productKey] === color;

      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
      button.setAttribute('aria-disabled', String(variant?.available === false));
      button.disabled = variant?.available === false;
    });

    const selectedColors = this.state.selectedKeys.map((key) => this.state.colors[key]);
    const sameColor = selectedColors.length > 0
      && selectedColors.every((color) => color && color === selectedColors[0]);
    this.querySelectorAll('[data-bulk-color]').forEach((button) => {
      const color = button.dataset.bulkColor || '';
      const isAvailable = this.state.selectedKeys.length > 0
        && this.state.selectedKeys.every((key) => this.getProduct(key)?.variants.some(
          (variant) => variant.color === color && variant.available !== false
        ));
      const isSelected = sameColor && color === selectedColors[0];

      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
      button.setAttribute('aria-disabled', String(!isAvailable));
      if (button instanceof HTMLButtonElement) button.disabled = !isAvailable;
    });
  }

  renderPreview() {
    const media = this.querySelector('[data-preview-media]');
    if (!(media instanceof HTMLElement)) return;

    const preset = this.findPresetForSelection();
    const preview = this.querySelector('[data-preview]');
    const aspectRatio = Number(preset?.imageAspectRatio);
    if (preview instanceof HTMLElement) {
      preview.style.setProperty(
        '--atl-bundle-preview-ratio',
        Number.isFinite(aspectRatio) && aspectRatio > 0 ? String(aspectRatio) : ''
      );
    }

    const imageUrl = preset?.image
      || preset?.imageSrcset?.split(',')[0]?.trim().split(/\s+/)[0]
      || '';
    const imageSrcset = preset?.imageSrcset || '';
    const imageSizes = preset?.imageSizes || '';
    const imageKey = JSON.stringify([imageUrl, imageSrcset, imageSizes]);
    const activeImage = media.querySelector('img[data-preview-active]');
    const pendingImage = media.querySelector('img[data-preview-target]');

    if (
      imageUrl && (
        pendingImage?.dataset.previewKey === imageKey ||
        (!pendingImage && activeImage?.dataset.previewKey === imageKey)
      )
    ) {
      return;
    }

    if (imageUrl && activeImage?.dataset.previewKey === imageKey && pendingImage) {
      this.previewRequestId += 1;
      this.clearPreviewTimers();
      this.cancelPreviewImageWait();
      pendingImage.remove();
      activeImage.classList.remove('atelea-bundle-builder__preview-image--previous');
      media.removeAttribute('data-previewing');
      media.removeAttribute('data-delayed');
      media.removeAttribute('data-state');
      media.removeAttribute('aria-busy');
      return;
    }

    if (!imageUrl) {
      this.previewRequestId += 1;
      this.clearPreviewTimers();
      this.cancelPreviewImageWait();
      media.replaceChildren();
      media.setAttribute('hidden', '');
      media.removeAttribute('data-state');
      media.removeAttribute('data-delayed');
      media.removeAttribute('data-previewing');
      media.removeAttribute('aria-busy');
    } else {
      const requestId = ++this.previewRequestId;
      this.clearPreviewTimers();
      this.cancelPreviewImageWait();

      if (media.dataset.previewing === 'true' && pendingImage) {
        activeImage?.remove();
        pendingImage.classList.remove('atelea-bundle-builder__preview-image--target');
        pendingImage.classList.remove('atelea-bundle-builder__preview-image--previous');
        pendingImage.removeAttribute('data-preview-target');
        pendingImage.removeAttribute('aria-hidden');
        pendingImage.dataset.previewActive = '';
      } else {
        pendingImage?.remove();
      }

      media.removeAttribute('data-previewing');
      media.removeAttribute('data-delayed');
      media.removeAttribute('data-state');
      media.removeAttribute('aria-busy');

      const previousImage = media.querySelector('img[data-preview-active]');
      previousImage?.classList.remove('atelea-bundle-builder__preview-image--previous');

      const targetImage = document.createElement('img');
      targetImage.className =
        'atelea-bundle-builder__preview-image atelea-bundle-builder__preview-image--target';
      targetImage.dataset.previewTarget = '';
      targetImage.dataset.previewKey = imageKey;
      targetImage.setAttribute('aria-hidden', 'true');
      targetImage.alt = preset?.imageAlt || preset?.title || '';
      targetImage.loading = 'eager';
      targetImage.decoding = 'async';
      targetImage.fetchPriority = 'high';
      if (imageSrcset) targetImage.srcset = imageSrcset;
      targetImage.sizes = imageSizes;
      media.append(targetImage);
      media.removeAttribute('hidden');

      if (previousImage) {
        previousImage.classList.add('atelea-bundle-builder__preview-image--previous');
        media.dataset.state = 'loading';
        media.setAttribute('aria-busy', 'true');

        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.previewDelayTimer = window.setTimeout(() => {
            if (requestId === this.previewRequestId) {
              media.dataset.delayed = 'true';
            }
          }, 180);
        }
      }

      const controller = new AbortController();
      this.previewImageAbortController = controller;
      targetImage.src = imageUrl;
      this.waitForPreviewImage(targetImage, controller.signal).then((loaded) => {
        if (requestId !== this.previewRequestId) return;
        if (this.previewImageAbortController === controller) {
          this.previewImageAbortController = null;
        }

        window.clearTimeout(this.previewDelayTimer);
        this.previewDelayTimer = 0;
        media.removeAttribute('data-delayed');

        if (!loaded) {
          targetImage.remove();
          previousImage?.classList.remove('atelea-bundle-builder__preview-image--previous');
          media.removeAttribute('data-state');
          media.removeAttribute('aria-busy');
          return;
        }

        if (!previousImage) {
          targetImage.classList.remove('atelea-bundle-builder__preview-image--target');
          targetImage.removeAttribute('data-preview-target');
          targetImage.removeAttribute('aria-hidden');
          targetImage.dataset.previewActive = '';
          media.removeAttribute('data-state');
          media.removeAttribute('aria-busy');
          return;
        }

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          previousImage.remove();
          targetImage.classList.remove('atelea-bundle-builder__preview-image--target');
          targetImage.removeAttribute('data-preview-target');
          targetImage.removeAttribute('aria-hidden');
          targetImage.dataset.previewActive = '';
          media.removeAttribute('data-state');
          media.removeAttribute('aria-busy');
          return;
        }

        window.requestAnimationFrame(() => {
          if (requestId !== this.previewRequestId) return;
          media.dataset.previewing = 'true';
          media.dataset.state = 'preview';
          this.previewFinishTimer = window.setTimeout(() => {
            if (requestId !== this.previewRequestId) return;
            previousImage.remove();
            targetImage.classList.remove('atelea-bundle-builder__preview-image--target');
            targetImage.removeAttribute('data-preview-target');
            targetImage.removeAttribute('aria-hidden');
            targetImage.dataset.previewActive = '';
            media.removeAttribute('data-previewing');
            media.removeAttribute('data-state');
            media.removeAttribute('aria-busy');
            this.previewFinishTimer = 0;
          }, 200);
        });
      });
    }
  }

  clearPreviewTimers() {
    window.clearTimeout(this.previewDelayTimer);
    window.clearTimeout(this.previewFinishTimer);
    this.previewDelayTimer = 0;
    this.previewFinishTimer = 0;
  }

  cancelPreviewImageWait() {
    this.previewImageAbortController?.abort();
    this.previewImageAbortController = null;
  }

  /** @param {HTMLImageElement} image @param {AbortSignal} signal @returns {Promise<boolean>} */
  waitForPreviewImage(image, signal) {
    return new Promise((resolve) => {
      const settle = (loaded) => {
        signal.removeEventListener('abort', handleAbort);
        image.removeEventListener('load', handleLoad);
        image.removeEventListener('error', handleError);
        resolve(loaded);
      };
      const handleAbort = () => settle(false);
      const handleLoad = () => {
        try {
          Promise.resolve(image.decode?.())
            .then(() => settle(image.naturalWidth > 0))
            .catch(() => settle(image.naturalWidth > 0));
        } catch {
          settle(image.naturalWidth > 0);
        }
      };
      const handleError = () => settle(false);

      if (signal.aborted) {
        settle(false);
        return;
      }
      signal.addEventListener('abort', handleAbort, { once: true });
      if (image.complete) {
        if (image.naturalWidth > 0) handleLoad();
        else settle(false);
        return;
      }
      image.addEventListener('load', handleLoad, { once: true });
      image.addEventListener('error', handleError, { once: true });
    });
  }

  /** @param {SelectionMatch | null} match */
  renderFlow(match) {
    const mobile = this.isMobileViewport();
    const hasPreset = Boolean(this.findPresetForSelection());
    const hasSelection = this.state.selectedKeys.length >= 2;
    const stepReady = {
      1: hasSelection && hasPreset,
      2: Boolean(match?.valid),
    };

    this.querySelectorAll('[data-builder-step]').forEach((section) => {
      const step = Number(section.getAttribute('data-builder-step'));
      const toggle = section.querySelector('[data-builder-step-toggle]');
      const content = section.querySelector('[data-builder-step-content]');
      const blocked = mobile && (
        (step === 2 && !hasPreset)
        || (step === 3 && !match?.valid)
      );
      const open = !mobile || (this.flow.openStep === step && !blocked);
      const complete = (step === 1 && hasPreset)
        || (step === 2 && Boolean(match?.valid))
        || (step === 3 && mobile && this.flow.openStep === 3);

      section.dataset.state = open ? 'open' : 'closed';
      section.dataset.complete = String(complete);
      content?.toggleAttribute('hidden', !open);
      if (toggle instanceof HTMLButtonElement) {
        toggle.tabIndex = mobile ? 0 : -1;
        toggle.disabled = blocked;
        toggle.setAttribute('aria-expanded', String(open));
        toggle.setAttribute('aria-controls', content?.id || '');
        if (blocked) {
          toggle.setAttribute('aria-disabled', 'true');
        } else {
          toggle.removeAttribute('aria-disabled');
        }
      }
    });

    this.querySelectorAll('[data-builder-continue]').forEach((control) => {
      const step = Number(control.getAttribute('data-builder-continue'));
      const enabled = step === 1 ? stepReady[1] : step === 2 && stepReady[2];
      control.toggleAttribute('hidden', !mobile);
      if (control instanceof HTMLButtonElement) control.disabled = !enabled;
    });

    const stepStatus = this.querySelector('[data-builder-step-status="2"]');
    if (stepStatus) {
      let message = '';
      const notice = Boolean(mobile && this.flow.openStep === 2 && !match?.valid);
      if (notice && !match?.hasColors) {
        message = this.data.messages.chooseEveryColor;
      } else if (notice && match?.hasUnavailableVariant) {
        message = this.data.messages.soldOutNotice;
      } else if (notice) {
        message = this.data.messages.unavailable;
      }
      stepStatus.textContent = message;
      stepStatus.classList.toggle('is-notice', Boolean(message));
    }

    const summaryVisible = !mobile || this.flow.openStep === 3;
    this.querySelector('[data-summary-panel]')?.removeAttribute('hidden');
    this.querySelector('[data-summary-card]')?.toggleAttribute('hidden', !summaryVisible);
  }

  isMobileViewport() {
    return this.flowMediaQuery?.matches ?? window.matchMedia('(max-width: 749px)').matches;
  }

  /** @param {SelectionMatch | null} match */
  renderSummary(match) {
    const hasSelection = this.state.selectedKeys.length >= 2;
    const empty = this.querySelector('[data-summary-empty]');
    const selected = this.querySelector('[data-summary-selected]');
    const title = this.querySelector('[data-summary-title]');
    const lines = this.querySelector('[data-summary-lines]');
    const original = this.querySelector('[data-summary-original]');
    const total = this.querySelector('[data-summary-total]');
    const savingAmount = this.querySelector('[data-summary-saving-amount]');
    const savingRate = this.querySelector('[data-summary-saving-rate]');

    empty?.toggleAttribute('hidden', hasSelection);
    selected?.toggleAttribute('hidden', !hasSelection);
    if (!hasSelection) {
      this.clearStatus();
      return;
    }

    if (title) title.textContent = match?.preset?.title || this.data.messages.summaryTitle;
    if (lines) {
      lines.replaceChildren(
        ...this.state.selectedKeys.map((key) => {
          const line = document.createElement('li');
          const product = this.getProduct(key);
          const color = this.state.colors[key];

          line.className = 'atelea-bundle-builder__summary-line';
          line.dataset.colorSlug = color ? this.slugify(color) : '';
          const copy = document.createElement('span');
          copy.className = 'atelea-bundle-builder__summary-line-copy';

          const productName = document.createElement('strong');
          productName.textContent = product?.title || '';
          copy.append(productName);

          if (color) {
            const colorName = document.createElement('span');
            colorName.textContent = color;
            copy.append(colorName);
          }

          line.append(copy);
          return line;
        })
      );
    }

    if (match?.valid && match.parentVariant) {
      const pricing = this.getDiscountedPrices(match.parentVariant.price, match.discount);

      if (original) original.textContent = this.formatMoney(pricing.regular * 100);
      if (total) total.textContent = this.formatMoney(pricing.final * 100);
      if (savingAmount) savingAmount.textContent = '−' + this.formatMoney(pricing.saved * 100);
      if (savingRate) savingRate.textContent = ' (' + Math.round(match.discount * 100) + '%)';
      this.clearStatus();
      return;
    }

    if (original) original.textContent = '—';
    if (total) total.textContent = '—';
    if (savingAmount) savingAmount.textContent = '—';
    if (savingRate) savingRate.textContent = '';

    if (!match?.hasColors) {
      this.setStatus(this.data.messages.chooseEveryColor, '');
    } else if (match.hasUnavailableVariant) {
      this.setStatus(this.data.messages.soldOutNotice, 'notice');
    } else {
      this.setStatus(this.data.messages.unavailable, 'notice');
    }
  }

  /** @param {boolean} enabled */
  renderAddButtons(enabled) {
    const match = this.getSelectionMatch();
    const isPrelaunch = this.dataset.ateleaPrelaunchMode === 'true';
    const flowReady = !this.isMobileViewport() || this.flow.openStep === 3;
    const canAdd = enabled && flowReady;
    this.querySelectorAll('[data-add-bundle]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      const label = button.querySelector('[data-add-label]');
      const loader = button.querySelector('[data-add-loader]');
      const wishlistItems = isPrelaunch && canAdd ? this.buildWishlistItems(match) : [];
      const wishlistSaved = wishlistItems.length > 0
        && wishlistItems.every((item) => this.wishlistKeys.has(this.getWishlistKey(item)));
      let idleText = this.data.messages.add;
      if (isPrelaunch) {
        idleText = wishlistSaved
          ? this.data.messages.removeWishlist
          : this.data.messages.addWishlist;
      } else if (!canAdd && match?.hasColors) {
        idleText = match.hasUnavailableVariant
          ? this.data.messages.soldOut
          : this.data.messages.unavailableAction;
      }
      const loadingText = this.data.messages.adding;

      button.disabled = !canAdd
        || (!isPrelaunch && this.isAdding);
      button.setAttribute('aria-busy', String(!isPrelaunch && this.isAdding));
      button.dataset.ateleaLoading = String(!isPrelaunch && this.isAdding);
      if (isPrelaunch) {
        button.classList.toggle('is-saved', wishlistSaved);
        button.setAttribute('aria-pressed', String(wishlistSaved));
      } else {
        button.classList.remove('is-saved');
        button.removeAttribute('aria-pressed');
      }
      if (!isPrelaunch && this.isAdding) {
        if (label) {
          label.textContent = '';
          label.setAttribute('hidden', '');
        } else {
          button.textContent = '';
        }
        loader?.removeAttribute('hidden');
        button.setAttribute('aria-label', loadingText);
        return;
      }

      if (label) {
        label.textContent = idleText;
        label.removeAttribute('hidden');
      } else {
        button.textContent = idleText;
      }
      loader?.setAttribute('hidden', '');
      if (isPrelaunch) {
        button.setAttribute('aria-label', idleText);
      } else {
        button.removeAttribute('aria-label');
      }
    });
  }

  /** @returns {SelectionMatch | null} */
  getSelectionMatch() {
    if (this.state.selectedKeys.length < 2) return null;

    const preset = this.findPresetForSelection();
    const parent = preset ? this.getParent(preset.parentHandle) : null;
    const products = this.state.selectedKeys.map((key) => this.getProduct(key));
    const hasColors = products.every((product, index) => {
      const key = this.state.selectedKeys[index];
      return product && this.state.colors[key];
    });
    if (!preset || !parent || products.some((product) => !product)) {
      return { hasColors, valid: false, preset };
    }

    const componentVariants = products.map((product, index) => product.variants.find(
      (variant) => variant.color === this.state.colors[this.state.selectedKeys[index]]
    ));
    const parentVariant = hasColors
      ? parent.variants.find((variant) => variant.options
        .slice(0, this.state.selectedKeys.length)
        .every((color, index) => color === this.state.colors[this.state.selectedKeys[index]]))
      : null;
    const componentTotal = componentVariants.reduce(
      (sum, variant) => sum + (variant?.price || 0),
      0
    );
    const componentsAvailable = componentVariants.every((variant) => variant?.available !== false);
    const hasUnavailableVariant = componentVariants.some(
      (variant) => variant?.available === false
    ) || parentVariant?.available === false;
    const valid = Boolean(
      hasColors
      && parentVariant
      && componentsAvailable
      && parentVariant.available !== false
      && parentVariant.price === componentTotal
    );

    return {
      componentTotal,
      discount: this.state.selectedKeys.length === 3
        ? this.data.completeDiscount
        : this.data.pairDiscount,
      hasColors,
      hasUnavailableVariant,
      parentVariant,
      preset,
      valid,
    };
  }

  /** @returns {Preset | null} */
  findPresetForSelection() {
    return this.data.presets.find((preset) => {
      const keys = this.getPresetProductKeys(preset);
      return keys.length === this.state.selectedKeys.length
        && keys.every((key) => this.state.selectedKeys.includes(key));
    }) || null;
  }

  /** @param {Preset} preset @returns {string[]} */
  getPresetProductKeys(preset) {
    return preset.components
      .filter(Boolean)
      .map((handle) => this.data.products.find((product) => product.handle === handle)?.key)
      .filter((key) => typeof key === 'string');
  }

  /** @param {string} key @returns {ProductData | null} */
  getProduct(key) {
    return this.data.products.find((product) => product.key === key && product.handle) || null;
  }

  /** @param {string} handle @returns {ParentData | null} */
  getParent(handle) {
    return this.data.parents.find((parent) => parent.handle === handle) || null;
  }

  /** @returns {ColorOption[]} */
  getColors() {
    const colors = [];
    const seen = new Set();
    this.data.products.forEach((product) => {
      product.variants.forEach((variant) => {
        if (!variant.color || seen.has(variant.color)) return;
        seen.add(variant.color);
        colors.push({ name: variant.color, slug: this.slugify(variant.color) });
      });
    });
    return colors;
  }

  /** @param {string} value */
  slugify(value) {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /** @param {number} cents */
  formatMoney(cents) {
    return MONEY_FORMATTER.format(Math.round(cents / 100));
  }

  /** @param {number} cents @param {number} discount */
  getDiscountedPrices(cents, discount) {
    const regular = Math.round(cents / 100);
    const final = Math.round((cents * (1 - discount)) / 100);
    return { final, regular, saved: regular - final };
  }

  /** @param {string} message @param {string} tone */
  setStatus(message, tone) {
    const status = this.querySelector('[data-builder-status]');
    if (!status) return;

    status.textContent = message;
    status.classList.toggle('is-error', tone === 'error');
    status.classList.toggle('is-notice', tone === 'notice');
    status.classList.toggle('is-success', tone === 'success');
  }

  clearStatus() {
    this.setStatus('', '');
  }

  animateFlyToCart() {
    const source = this.querySelector('[data-add-bundle]');
    const destination = document.querySelector('.header-actions__cart-icon');
    const selectedKey = this.state.selectedKeys[0];
    const sourceImage = [...this.querySelectorAll('[data-color-row]')]
      .find((row) => row.getAttribute('data-color-row') === selectedKey)
      ?.querySelector('img');
    const image = sourceImage instanceof HTMLImageElement
      ? sourceImage.currentSrc || sourceImage.src
      : '';

    if (!(source instanceof HTMLButtonElement) || !destination || !image) return;

    const flyer = /** @type {HTMLElement & {source: Element, destination: Element}} */ (
      document.createElement('fly-to-cart')
    );
    flyer.classList.add('fly-to-cart--main');
    flyer.style.setProperty('background-image', `url(${image})`);
    flyer.style.setProperty('--start-opacity', '0');
    flyer.source = source;
    flyer.destination = destination;
    document.body.appendChild(flyer);
  }

  getCartSectionIds() {
    return [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((component) => component.getAttribute('data-section-id'))
      .filter((sectionId) => sectionId);
  }

  /**
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} deferredUpdate
   * @param {CartData} cart
   * @param {boolean} didError
   * @param {CartResponse['sections']} sections
   */
  resolveCartUpdate(deferredUpdate, cart, didError, sections) {
    deferredUpdate.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError,
        itemCount: this.getItemCount(cart),
        items: cart.items,
        sections,
        source: 'atelea-bundle-builder',
      },
    });
  }

  /** @param {string} error @param {CartResponse} response */
  dispatchCartError(error, response) {
    this.dispatchEvent(
      new CartErrorEvent({
        code: 'INVALID',
        detail: {
          description: response.description,
        },
        error,
      })
    );
  }

  /** @param {CartResponse} response */
  hasCartError(response) {
    return Boolean(response.status || response.errors);
  }

  /** @param {CartResponse} response @param {string} fallback */
  getCartErrorMessage(response, fallback) {
    if (typeof response.description === 'string' && response.description) {
      return response.description;
    }
    if (typeof response.message === 'string' && response.message) return response.message;
    if (typeof response.errors === 'string' && response.errors) return response.errors;
    return fallback;
  }

  async addBundleToCart() {
    if (this.isAdding) return;

    const match = this.getSelectionMatch();
    if (!match?.valid || !match.parentVariant) {
      this.render();
      return;
    }

    const variantId = String(match.parentVariant.id);
    this.animateFlyToCart();
    this.isAdding = true;
    this.renderAddButtons(true);
    this.clearStatus();

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    let didDispatchUpdate = false;

    try {
      const cartSectionIds = this.getCartSectionIds();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity: 1 }],
          promise: deferredEventPromise.promise,
        })
      );
      didDispatchUpdate = true;

      const response = await fetch(Theme.routes.cart_add_url, {
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: cartSectionIds.join(','),
          sections_url: window.location.pathname,
        }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
      });
      const addResult = await response.json();

      if (this.hasCartError(addResult)) {
        const error = this.getCartErrorMessage(addResult, this.data.messages.addError);
        this.dispatchCartError(error, addResult);
        const cart = await this.refreshCart();
        this.resolveCartUpdate(deferredEventPromise, cart, true, undefined);
        this.setStatus(error, 'error');
        return;
      }

      const cart = await this.refreshCart();
      this.resolveCartUpdate(deferredEventPromise, cart, false, addResult.sections);
    } catch (error) {
      console.error('[bundle-builder] Add to cart failed:', error);
      if (didDispatchUpdate) deferredEventPromise.reject(error);

      const errorMessage = this.data.messages.addError;
      this.dispatchEvent(
        new CartErrorEvent({
          code: 'SERVICE_UNAVAILABLE',
          error: errorMessage,
        })
      );
      this.setStatus(errorMessage, 'error');
    } finally {
      this.isAdding = false;
      this.renderAddButtons(Boolean(this.getSelectionMatch()?.valid));
    }
  }

  /** @param {SelectionMatch | null} match @returns {WishlistItemPayload[]} */
  buildWishlistItems(match) {
    if (!match?.valid) return [];

    /** @type {WishlistItemPayload[]} */
    const items = [];
    this.state.selectedKeys.forEach((key) => {
      const product = this.getProduct(key);
      const color = this.state.colors[key];
      const variant = product?.variants.find((item) => item.color === color);
      if (!product || !variant) return;

      items.push({
        productId: product.productId || '',
        productHandle: product.handle,
        productTitle: product.title,
        productUrl: product.url || `/products/${product.handle}`,
        variantId: String(variant.id),
        variantTitle: color || '',
        price: variant.price,
        image: variant.image || '',
      });
    });
    return items;
  }

  /** @param {WishlistItemPayload} item @returns {string} */
  getWishlistKey(item) {
    return `${item.productHandle}:${item.variantId || 'default'}`;
  }

  toggleBundleWishlist() {
    const match = this.getSelectionMatch();
    if (!match?.valid) {
      this.render();
      return;
    }

    const items = this.buildWishlistItems(match);

    if (items.length !== this.state.selectedKeys.length) {
      this.setStatus(this.data.messages.addError, 'error');
      return;
    }

    window.dispatchEvent(
      new CustomEvent('atelea:wishlist:toggle', {
        detail: { items, openPrompt: true },
      })
    );
  }

  async refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    const response = await fetch(Theme.routes.cart_url + '.json', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Cart refresh failed: ' + response.status);
    return response.json();
  }

  /** @param {CartData} cart */
  getItemCount(cart) {
    return cart.item_count
      || cart.itemCount
      || cart.items?.reduce((sum, item) => sum + item.quantity, 0)
      || 0;
  }
}

if (!customElements.get('atelea-bundle-builder')) {
  customElements.define('atelea-bundle-builder', AteleaBundleBuilder);
}

/** @typedef {{name: string, slug: string}} ColorOption */
/**
 * @typedef {object} CartResponse
 * @property {number} [status]
 * @property {string} [message]
 * @property {string} [description]
 * @property {unknown} [errors]
 * @property {Record<string, string>} [sections]
 */
/**
 * @typedef {object} ProductVariant
 * @property {string|number} id
 * @property {string} color
 * @property {number} price
 * @property {boolean} available
 * @property {string} [image]
 */
/**
 * @typedef {object} ProductData
 * @property {string} key
 * @property {string} [productId]
 * @property {string} handle
 * @property {string} title
 * @property {string} [url]
 * @property {ProductVariant[]} variants
 */
/**
 * @typedef {object} Preset
 * @property {string} id
 * @property {string} title
 * @property {boolean} recommended
 * @property {string} parentHandle
 * @property {string[]} components
 * @property {string} [image]
 * @property {number} [imageAspectRatio]
 * @property {string} [imageSrcset]
 * @property {string} [imageSizes]
 * @property {string} [imageAlt]
 */
/** @typedef {{handle: string, variants: ParentVariant[]}} ParentData */
/**
 * @typedef {object} ParentVariant
 * @property {string|number} id
 * @property {number} price
 * @property {boolean} available
 * @property {string[]} options
 */
/**
 * @typedef {object} BuilderData
 * @property {ProductData[]} products
 * @property {Preset[]} presets
 * @property {ParentData[]} parents
 * @property {number} pairDiscount
 * @property {number} completeDiscount
 * @property {Record<string, string>} messages
 */
/**
 * @typedef {object} WishlistItemPayload
 * @property {string} productId
 * @property {string} productHandle
 * @property {string} productTitle
 * @property {string} productUrl
 * @property {string} variantId
 * @property {string} variantTitle
 * @property {number} price
 * @property {string} image
 */
/**
 * @typedef {object} SelectionMatch
 * @property {number} [componentTotal]
 * @property {number} [discount]
 * @property {boolean} hasColors
 * @property {boolean} [hasUnavailableVariant]
 * @property {ParentVariant|null} [parentVariant]
 * @property {Preset|null} preset
 * @property {boolean} valid
 */
/**
 * @typedef {object} CartItem
 * @property {string} key
 * @property {number} quantity
 * @property {string|number} id
 * @property {number|string} final_line_price
 * @property {string|number} [variant_id]
 * @property {string|number} [variantId]
 */
/**
 * @typedef {import('@shopify/events').CartAjaxResponse & {
 *   itemCount?: number,
 *   items: CartItem[]
 * }} CartData
 */
/**
 * @typedef {object} WishlistStateItem
 * @property {string} productHandle
 * @property {string|number} variantId
 */
/** @typedef {{items?: WishlistStateItem[]}} WishlistChangedDetail */
